import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Users, 
  Building2, 
  Plus, 
  Pencil, 
  UserX,
  UserCheck,
  Upload,
  Shield,
  AlertTriangle,
  Monitor,
  Clock,
  Key,
  CheckCircle,
  XCircle,
  Eye,
  Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { formatCurrency } from '../lib/utils';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyUser = {
  first_name: '',
  last_name: '',
  email: '',
  role: 'comptable',
  hourly_rate: 0,
  password: ''
};

const emptyCompany = {
  company_name: 'Performance Transmission',
  address: '',
  postal_code: '',
  city: '',
  phone: '',
  email: '',
  iban: 'CH65 0076 7000 U542 2251 1',
  vat_number: '',
  logo_base64: '',
  bank_name: 'BCV Morges',
  bank_po_box: 'Case Postale 144',
  bank_address: 'CH-1110 Morges (Suisse)',
  clearing: '767',
  swift: 'BCVLCH2LXXX',
  payment_terms: '30 jours net',
  winbiz_account_debtor: '',
  winbiz_account_product: '',
  winbiz_payment_method_code: '',
  winbiz_client_prefix: 'PT'
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// ============== SECURITY TAB COMPONENT ==============
const SecurityTab = () => {
  const [stats, setStats] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      const [statsRes, loginRes, sessionsRes, alertsRes, auditRes] = await Promise.all([
        axios.get(`${API_URL}/security/stats`),
        axios.get(`${API_URL}/security/login-history?limit=50`),
        axios.get(`${API_URL}/security/active-sessions`),
        axios.get(`${API_URL}/security/alerts`),
        axios.get(`${API_URL}/security/audit-logs?limit=50`)
      ]);
      setStats(statsRes.data);
      setLoginHistory(loginRes.data);
      setActiveSessions(sessionsRes.data);
      setAlerts(alertsRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données de sécurité');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/security/alerts/${alertId}/acknowledge`);
      toast.success('Alerte acquittée');
      fetchSecurityData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/security/sessions/${sessionId}`);
      toast.success('Session terminée');
      fetchSecurityData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Security Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <CheckCircle size={20} />
            </div>
            <div>
              <p className="text-xl font-bold">{stats?.total_logins_24h || 0}</p>
              <p className="text-xs text-slate-500">Connexions (24h)</p>
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50 text-red-600">
              <XCircle size={20} />
            </div>
            <div>
              <p className="text-xl font-bold">{stats?.failed_logins_24h || 0}</p>
              <p className="text-xs text-slate-500">Échecs (24h)</p>
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Monitor size={20} />
            </div>
            <div>
              <p className="text-xl font-bold">{stats?.active_sessions || 0}</p>
              <p className="text-xs text-slate-500">Sessions actives</p>
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <AlertTriangle size={20} />
            </div>
            <div>
              <p className="text-xl font-bold">{stats?.pending_alerts || 0}</p>
              <p className="text-xs text-slate-500">Alertes</p>
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-100 text-slate-600">
              <Shield size={20} />
            </div>
            <div>
              <p className="text-xl font-bold">{stats?.blocked_accounts || 0}</p>
              <p className="text-xs text-slate-500">Comptes bloqués</p>
            </div>
          </div>
        </div>
      </div>

      {/* Blocked accounts warning */}
      {stats?.blocked_emails?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 mb-2">
            <AlertTriangle size={18} />
            <span className="font-semibold">Comptes temporairement bloqués</span>
          </div>
          <p className="text-sm text-red-700">
            Les comptes suivants sont bloqués suite à trop de tentatives de connexion échouées :
          </p>
          <ul className="mt-2 text-sm text-red-700">
            {stats.blocked_emails.map(email => (
              <li key={email}>• {email}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Section selector */}
      <div className="filter-pills">
        {[
          { id: 'overview', label: 'Alertes' },
          { id: 'sessions', label: 'Sessions actives' },
          { id: 'logins', label: 'Historique connexions' },
          { id: 'audit', label: 'Journal d\'audit' }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`filter-pill ${activeSection === section.id ? 'active' : ''}`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Alerts section */}
      {activeSection === 'overview' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Alertes de sécurité</h3>
          </div>
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Shield size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Aucune alerte de sécurité</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map(alert => (
                <div key={alert.id} className={`p-4 ${alert.acknowledged ? 'bg-slate-50' : 'bg-amber-50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={alert.acknowledged ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}>
                          {alert.type === 'suspicious_login' ? 'Connexion suspecte' : alert.type}
                        </Badge>
                        <span className="text-xs text-slate-500">{formatDateTime(alert.timestamp)}</span>
                      </div>
                      <p className="text-sm font-medium">{alert.email}</p>
                      <p className="text-xs text-slate-500">
                        IP: {alert.ip_address} • 
                        {alert.reason === 'new_ip' ? ' Nouvelle adresse IP' : ' Nouvel appareil'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 truncate max-w-md">{alert.user_agent}</p>
                    </div>
                    {!alert.acknowledged && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAcknowledgeAlert(alert.id)}
                        className="rounded-lg"
                      >
                        Acquitter
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active sessions section */}
      {activeSection === 'sessions' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Sessions actives</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>IP</th>
                  <th>Appareil</th>
                  <th>Connecté depuis</th>
                  <th>Dernière activité</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {activeSessions.map(session => (
                  <tr key={session.id}>
                    <td>
                      <div>
                        <p className="font-medium">{session.user_name}</p>
                        <p className="text-xs text-slate-500">{session.user_email}</p>
                      </div>
                    </td>
                    <td className="font-mono text-sm">{session.ip_address}</td>
                    <td className="text-xs max-w-[200px] truncate">{session.user_agent}</td>
                    <td className="text-sm">{formatDateTime(session.created_at)}</td>
                    <td className="text-sm">{formatDateTime(session.last_activity)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTerminateSession(session.id)}
                        className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        Terminer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Login history section */}
      {activeSection === 'logins' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Historique des connexions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Statut</th>
                  <th>IP</th>
                  <th>Date</th>
                  <th>Raison échec</th>
                </tr>
              </thead>
              <tbody>
                {loginHistory.map((attempt, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{attempt.email}</td>
                    <td>
                      <Badge className={attempt.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {attempt.success ? 'Succès' : 'Échec'}
                      </Badge>
                    </td>
                    <td className="font-mono text-sm">{attempt.ip_address}</td>
                    <td className="text-sm">{formatDateTime(attempt.timestamp)}</td>
                    <td className="text-sm text-slate-500">
                      {attempt.failure_reason === 'wrong_password' && 'Mot de passe incorrect'}
                      {attempt.failure_reason === 'user_not_found' && 'Utilisateur inconnu'}
                      {attempt.failure_reason === 'rate_limited' && 'Trop de tentatives'}
                      {attempt.failure_reason === 'account_disabled' && 'Compte désactivé'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit logs section */}
      {activeSection === 'audit' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Journal d'audit</h3>
          </div>
          {auditLogs.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Eye size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Aucune action enregistrée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Action</th>
                    <th>Ressource</th>
                    <th>IP</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td className="font-medium">{log.user_name}</td>
                      <td>
                        <Badge className={
                          log.action === 'create' ? 'bg-emerald-100 text-emerald-700' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {log.action === 'create' ? 'Création' :
                           log.action === 'update' ? 'Modification' : 'Suppression'}
                        </Badge>
                      </td>
                      <td className="text-sm">{log.resource_type}</td>
                      <td className="font-mono text-sm">{log.ip_address}</td>
                      <td className="text-sm">{formatDateTime(log.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Security info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <Shield size={18} />
          Politique de sécurité active
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Tokens JWT expiration : 1 heure</li>
          <li>• Session timeout : 30 minutes d'inactivité</li>
          <li>• Blocage après 5 tentatives de connexion échouées (15 min)</li>
          <li>• Mot de passe : min 12 caractères, majuscule, chiffre, symbole</li>
          <li>• Historique des 5 derniers mots de passe</li>
          <li>• Refresh tokens avec rotation</li>
        </ul>
      </div>
    </div>
  );
};

// ============== USER PREFERENCES TAB ==============
const PreferencesTab = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    receive_reminders: true,
    two_fa_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, [user]);

  const fetchPreferences = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API_URL}/users/${user.id}/preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReminders = async () => {
    setSaving(true);
    try {
      const newValue = !preferences.receive_reminders;
      await axios.put(`${API_URL}/users/${user.id}/preferences?receive_reminders=${newValue}`);
      setPreferences(prev => ({ ...prev, receive_reminders: newValue }));
      toast.success(newValue ? 'Rappels activés' : 'Rappels désactivés');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle2FA = async () => {
    setSaving(true);
    try {
      const newValue = !preferences.two_fa_enabled;
      await axios.put(`${API_URL}/users/${user.id}/2fa?enable=${newValue}`);
      setPreferences(prev => ({ ...prev, two_fa_enabled: newValue }));
      toast.success(newValue ? '2FA activé' : '2FA désactivé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <h3 className="text-lg font-semibold text-slate-900">Notifications par email</h3>
        
        <div className="flex items-center justify-between py-4 border-b">
          <div>
            <p className="font-medium">Rappels de tâches</p>
            <p className="text-sm text-slate-500">Recevoir des rappels par email pour les tâches avec deadline proche (J-3, J-1, Jour J)</p>
          </div>
          <button
            onClick={handleToggleReminders}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences.receive_reminders ? 'bg-primary' : 'bg-slate-200'
            }`}
            data-testid="toggle-reminders"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              preferences.receive_reminders ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div className="flex items-center justify-between py-4 border-b">
          <div>
            <p className="font-medium">Notifications d'assignation</p>
            <p className="text-sm text-slate-500">Recevoir un email quand une tâche vous est assignée</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">Toujours actif</Badge>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-6">
        <h3 className="text-lg font-semibold text-slate-900">Sécurité du compte</h3>
        
        <div className="flex items-center justify-between py-4 border-b">
          <div>
            <p className="font-medium">Authentification à deux facteurs (2FA)</p>
            <p className="text-sm text-slate-500">Ajouter une couche de sécurité avec un code envoyé par email lors de la connexion</p>
          </div>
          <button
            onClick={handleToggle2FA}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences.two_fa_enabled ? 'bg-primary' : 'bg-slate-200'
            }`}
            data-testid="toggle-2fa"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              preferences.two_fa_enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div className="flex items-center justify-between py-4">
          <div>
            <p className="font-medium">Alertes de connexion</p>
            <p className="text-sm text-slate-500">Recevoir un email en cas de connexion depuis un nouvel appareil ou une nouvelle IP</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">Toujours actif</Badge>
        </div>
      </div>

      <div className="bg-slate-50 border rounded-lg p-4">
        <p className="text-sm text-slate-600">
          <strong>Note :</strong> Les notifications d'assignation et les alertes de connexion ne peuvent pas être désactivées pour des raisons de sécurité.
        </p>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============
const Parametres = () => {
  const [users, setUsers] = useState([]);
  const [companySettings, setCompanySettings] = useState(emptyCompany);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [userFormData, setUserFormData] = useState(emptyUser);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/settings/company`)
      ]);
      setUsers(usersRes.data);
      setCompanySettings(settingsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 12) {
      return 'Min 12 caractères requis';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Une majuscule requise';
    }
    if (!/[a-z]/.test(password)) {
      return 'Une minuscule requise';
    }
    if (!/\d/.test(password)) {
      return 'Un chiffre requis';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Un symbole requis (!@#$%^&*...)';
    }
    return '';
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password for new users
    if (!editMode) {
      const error = validatePassword(userFormData.password);
      if (error) {
        setPasswordError(error);
        return;
      }
    }
    
    try {
      if (editMode && selectedUser) {
        const updateData = {
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          role: userFormData.role,
          hourly_rate: userFormData.hourly_rate
        };
        await axios.put(`${API_URL}/users/${selectedUser.id}`, updateData);
        toast.success('Utilisateur mis à jour');
      } else {
        await axios.post(`${API_URL}/users`, userFormData);
        toast.success('Utilisateur créé (invitation envoyée)');
      }
      setShowUserForm(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await axios.put(`${API_URL}/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Utilisateur désactivé' : 'Utilisateur réactivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/settings/company`, companySettings);
      toast.success('Paramètres de la société mis à jour');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanySettings({ ...companySettings, logo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const resetUserForm = () => {
    setUserFormData(emptyUser);
    setEditMode(false);
    setSelectedUser(null);
    setPasswordError('');
  };

  const openEditUser = (user) => {
    setSelectedUser(user);
    setUserFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      hourly_rate: user.hourly_rate || 0,
      password: ''
    });
    setEditMode(true);
    setShowUserForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parametres-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="tab-users">
            <Users size={16} className="mr-2" />
            Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">
            <Building2 size={16} className="mr-2" />
            Société
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield size={16} className="mr-2" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">
            <Bell size={16} className="mr-2" />
            Mes préférences
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex justify-end">
            <Button 
              onClick={() => { resetUserForm(); setShowUserForm(true); }}
              className="rounded-lg"
              data-testid="new-user-button"
            >
              <Plus size={18} className="mr-2" />
              Nouvel utilisateur
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {users.length === 0 ? (
              <div className="empty-state">
                <Users size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">Aucun utilisateur</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table responsive-table" data-testid="users-table">
                  <thead>
                    <tr>
                      <th>Prénom</th>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Rôle</th>
                      <th>Coût horaire</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td data-label="Prénom">{user.first_name}</td>
                        <td data-label="Nom">{user.last_name}</td>
                        <td data-label="Email">{user.email}</td>
                        <td data-label="Rôle">
                          <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {user.role === 'admin' ? 'Administrateur' : 'Comptable'}
                          </Badge>
                        </td>
                        <td data-label="Coût horaire" className="tabular-nums">
                          {formatCurrency(user.hourly_rate || 0)}/h
                        </td>
                        <td data-label="Statut">
                          <Badge className={user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                            {user.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </td>
                        <td data-label="Actions">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditUser(user)}
                              className="rounded-lg"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleUserStatus(user)}
                              className={`rounded-lg ${user.is_active ? 'text-red-500 hover:text-red-600 hover:bg-red-50' : 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'}`}
                              data-testid={`toggle-user-${user.id}`}
                            >
                              {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Company Tab */}
        <TabsContent value="company">
          <form onSubmit={handleCompanySubmit} className="space-y-6">
            <div className="form-section">
              <h3 className="form-section-title">Informations de la société</h3>
              <p className="text-sm text-slate-500 mb-4">Ces informations apparaîtront sur les factures</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de la société</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                    data-testid="company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={companySettings.phone}
                    onChange={(e) => setCompanySettings({...companySettings, phone: e.target.value})}
                    data-testid="company-phone"
                  />
                </div>
              </div>
              
              <div className="space-y-2 mt-4">
                <Label>Adresse</Label>
                <Input
                  value={companySettings.address}
                  onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                  data-testid="company-address"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input
                    value={companySettings.postal_code}
                    onChange={(e) => setCompanySettings({...companySettings, postal_code: e.target.value})}
                    data-testid="company-postal-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input
                    value={companySettings.city}
                    onChange={(e) => setCompanySettings({...companySettings, city: e.target.value})}
                    data-testid="company-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={companySettings.email}
                    onChange={(e) => setCompanySettings({...companySettings, email: e.target.value})}
                    data-testid="company-email"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Informations bancaires et fiscales</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={companySettings.iban || ''}
                    onChange={(e) => setCompanySettings({...companySettings, iban: e.target.value})}
                    placeholder="CH..."
                    data-testid="company-iban"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Numéro TVA / IDE</Label>
                  <Input
                    value={companySettings.vat_number || ''}
                    onChange={(e) => setCompanySettings({...companySettings, vat_number: e.target.value})}
                    placeholder="CHE-..."
                    data-testid="company-vat"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom de la banque</Label>
                  <Input
                    value={companySettings.bank_name || ''}
                    onChange={(e) => setCompanySettings({...companySettings, bank_name: e.target.value})}
                    placeholder="Ex: BCV Morges"
                    data-testid="company-bank-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Case postale</Label>
                  <Input
                    value={companySettings.bank_po_box || ''}
                    onChange={(e) => setCompanySettings({...companySettings, bank_po_box: e.target.value})}
                    placeholder="Ex: Case Postale 144"
                    data-testid="company-bank-po-box"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse de la banque</Label>
                  <Input
                    value={companySettings.bank_address || ''}
                    onChange={(e) => setCompanySettings({...companySettings, bank_address: e.target.value})}
                    placeholder="Ex: CH-1110 Morges (Suisse)"
                    data-testid="company-bank-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clearing</Label>
                  <Input
                    value={companySettings.clearing || ''}
                    onChange={(e) => setCompanySettings({...companySettings, clearing: e.target.value})}
                    placeholder="Ex: 767"
                    data-testid="company-clearing"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SWIFT / BIC</Label>
                  <Input
                    value={companySettings.swift || ''}
                    onChange={(e) => setCompanySettings({...companySettings, swift: e.target.value})}
                    placeholder="Ex: BCVLCH2LXXX"
                    data-testid="company-swift"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conditions de paiement</Label>
                  <Input
                    value={companySettings.payment_terms || ''}
                    onChange={(e) => setCompanySettings({...companySettings, payment_terms: e.target.value})}
                    placeholder="Ex: 30 jours net"
                    data-testid="company-payment-terms"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Configuration WinBIZ</h3>
              <p className="text-xs text-slate-500 mb-4">
                Ces codes sont propres au dossier WinBIZ de Performance Transmission et sont utilisés pour l'export CSV des factures de vente (format bizexdoc).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Compte débiteur</Label>
                  <Input
                    value={companySettings.winbiz_account_debtor || ''}
                    onChange={(e) => setCompanySettings({...companySettings, winbiz_account_debtor: e.target.value})}
                    placeholder="Ex: 1100"
                    data-testid="company-winbiz-account-debtor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Compte produit</Label>
                  <Input
                    value={companySettings.winbiz_account_product || ''}
                    onChange={(e) => setCompanySettings({...companySettings, winbiz_account_product: e.target.value})}
                    placeholder="Ex: 3400"
                    data-testid="company-winbiz-account-product"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code méthode de paiement</Label>
                  <Input
                    value={companySettings.winbiz_payment_method_code || ''}
                    onChange={(e) => setCompanySettings({...companySettings, winbiz_payment_method_code: e.target.value})}
                    placeholder="Code WinBIZ"
                    data-testid="company-winbiz-payment-method"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Préfixe code adresse client</Label>
                  <Input
                    value={companySettings.winbiz_client_prefix || ''}
                    onChange={(e) => setCompanySettings({...companySettings, winbiz_client_prefix: e.target.value})}
                    placeholder="PT"
                    data-testid="company-winbiz-client-prefix"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Logo</h3>
              <div className="flex items-center gap-4">
                {companySettings.logo_base64 ? (
                  <div className="relative">
                    <img 
                      src={companySettings.logo_base64} 
                      alt="Logo" 
                      className="h-20 object-contain border rounded-lg p-2"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCompanySettings({...companySettings, logo_base64: ''})}
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-white border shadow-sm"
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-40 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center text-slate-400">
                    Aucun logo
                  </div>
                )}
                <div>
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors">
                      <Upload size={16} />
                      <span>Téléverser un logo</span>
                    </div>
                  </Label>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="company-logo-upload"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="rounded-lg" data-testid="save-company-settings">
                Enregistrer les paramètres
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <PreferencesTab />
        </TabsContent>
      </Tabs>

      {/* User Form Dialog */}
      <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Modifiez les informations de l\'utilisateur' : 'Un email d\'invitation sera envoyé'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={userFormData.first_name}
                  onChange={(e) => setUserFormData({...userFormData, first_name: e.target.value})}
                  required
                  data-testid="user-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={userFormData.last_name}
                  onChange={(e) => setUserFormData({...userFormData, last_name: e.target.value})}
                  required
                  data-testid="user-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                required
                disabled={editMode}
                data-testid="user-email"
              />
            </div>
            
            {!editMode && (
              <div className="space-y-2">
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => {
                    setUserFormData({...userFormData, password: e.target.value});
                    setPasswordError(validatePassword(e.target.value));
                  }}
                  required={!editMode}
                  data-testid="user-password"
                />
                {passwordError && (
                  <p className="text-xs text-red-500">{passwordError}</p>
                )}
                <p className="text-xs text-slate-500">
                  Min 12 caractères, majuscule, minuscule, chiffre, symbole
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={userFormData.role} onValueChange={(v) => setUserFormData({...userFormData, role: v})}>
                  <SelectTrigger data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrateur</SelectItem>
                    <SelectItem value="comptable">Comptable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Coût horaire (CHF)</Label>
                <Input
                  type="number"
                  value={userFormData.hourly_rate}
                  onChange={(e) => setUserFormData({...userFormData, hourly_rate: parseFloat(e.target.value) || 0})}
                  data-testid="user-hourly-rate"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserForm(false)} className="rounded-lg">
                Annuler
              </Button>
              <Button type="submit" className="rounded-lg" data-testid="user-form-submit">
                {editMode ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Parametres;
